import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import {
  Loader2, Sparkles, Copy, Check, Users, Coins,
  QrCode, ArrowRight, Share2, Zap,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * Profile — signed-in view showing referral stats + share link + history.
 *
 * Referral economics (server-side source of truth):
 *   • Referrer  → +10 credits per successful signup via ?ref=
 *   • Referee   → +5  credits on top of the 5 free signup credits
 */
export default function Profile() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Redirect to signup if not authenticated
  useEffect(() => {
    if (!authLoading && !user) navigate("/?intent=signup");
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setLoading(true);
      try {
        const [refRes, listRes] = await Promise.all([
          api.get("/me/referral"),
          api.get("/me/referral/list"),
        ]);
        setStats(refRes.data);
        setList(Array.isArray(listRes.data) ? listRes.data : []);
      } catch (e) {
        console.warn("Referral load failed", e);
        toast.error("Couldn't load your referral stats. Try again in a moment.");
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const shareUrl = useMemo(() => {
    if (!stats?.referral_code) return "";
    return `${window.location.origin}/?ref=${stats.referral_code}`;
  }, [stats?.referral_code]);

  const copy = useCallback(async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — try selecting the link manually.");
    }
  }, [shareUrl]);

  const share = useCallback(async () => {
    if (!shareUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join me on Geneo AI",
          text: "Generate cinematic Afro Sci-Fi imagery, videos, and voices with me on Geneo AI.",
          url: shareUrl,
        });
        return;
      } catch { /* user dismissed — fall through to copy */ }
    }
    copy();
  }, [shareUrl, copy]);

  if (authLoading || (!user && !authLoading)) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#F2C94C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] glyph-bg text-white">
      <Navbar />

      {/* Ambient gradient wash */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-0 right-0 top-16 h-[420px]"
        style={{
          background:
            "radial-gradient(50% 60% at 25% 20%, rgba(88,28,135,0.35), transparent 60%)," +
            "radial-gradient(40% 55% at 82% 18%, rgba(242,201,76,0.14), transparent 65%)",
        }}
      />

      <main className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12">
        {/* ── Header ────────────────────────────────────────────── */}
        <div className="mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F2C94C]/10 border border-[#F2C94C]/30 mb-3">
            <Users className="w-3 h-3 text-[#F2C94C]" />
            <span className="font-mono-hy text-[9px] text-[#F2C94C] tracking-[0.24em]">
              PROFILE · REFERRALS
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-3">
            Invite friends,{" "}
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: "linear-gradient(120deg, #F2C94C 0%, #E27D60 45%, #00E5FF 100%)" }}
            >
              earn credits
            </span>
          </h1>
          <p className="font-body text-base sm:text-lg text-white leading-relaxed max-w-2xl">
            You get <span className="text-[#F2C94C] font-semibold">+{stats?.reward_per_referral ?? 10} credits</span> every time a friend signs up through your link.
            They start with <span className="text-[#F2C94C] font-semibold">+{stats?.reward_referee_bonus ?? 5} bonus credits</span> on top of the standard 5 free.
          </p>
        </div>

        {loading ? (
          <div className="py-24 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-[#F2C94C] animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* ── Stats trio ─────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard
                testid="profile-stat-referrals"
                icon={<Users className="w-4 h-4" />}
                label="TOTAL REFERRALS"
                value={stats?.total_referrals ?? 0}
                accent="#F2C94C"
              />
              <StatCard
                testid="profile-stat-earned"
                icon={<Coins className="w-4 h-4" />}
                label="CREDITS EARNED"
                value={`+${stats?.credits_earned ?? 0}`}
                accent="#27AE60"
              />
              <StatCard
                testid="profile-stat-balance"
                icon={<Zap className="w-4 h-4" />}
                label="YOUR BALANCE"
                value={user?.credits ?? 0}
                accent="#00E5FF"
              />
            </div>

            {/* ── Share card ─────────────────────────────────── */}
            <div
              className="rounded-3xl border border-[#F2C94C]/25 p-5 sm:p-7"
              style={{
                background:
                  "radial-gradient(80% 100% at 0% 0%, rgba(88,28,135,0.35), transparent 60%)," +
                  "linear-gradient(160deg, #120E1F 0%, #0A0812 100%)",
              }}
              data-testid="profile-share-card"
            >
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-4 h-4 text-[#F2C94C]" />
                <span className="font-mono-hy text-[10px] text-[#F2C94C] tracking-[0.24em]">
                  YOUR REFERRAL LINK
                </span>
              </div>
              <div className="rounded-2xl border border-white/[0.08] bg-[#0A0A0A]/70 px-4 py-3.5 flex items-center gap-3">
                <span
                  data-testid="profile-referral-link"
                  className="flex-1 min-w-0 font-mono text-[13px] sm:text-[14px] text-white truncate"
                  title={shareUrl}
                >
                  {shareUrl.replace(/^https?:\/\//, "")}
                </span>
                <button
                  onClick={copy}
                  data-testid="profile-copy-link"
                  className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#F2C94C]/40 hover:border-[#F2C94C] hover:bg-[#F2C94C]/[0.08] font-mono-hy text-[9.5px] tracking-[0.18em] text-[#F2C94C] transition-all"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "COPIED" : "COPY"}
                </button>
              </div>
              <div className="mt-4 flex flex-col sm:flex-row gap-2">
                <button
                  onClick={share}
                  data-testid="profile-share-btn"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-[#F2C94C] hover:bg-[#F2D06B] text-[#0A0A0A] font-mono-hy text-[10px] tracking-[0.22em] font-bold hover:shadow-[0_0_28px_-4px_rgba(242,201,76,0.85)] transition-all"
                >
                  <Share2 className="w-4 h-4" />
                  SHARE LINK
                </button>
                <Link
                  to="/qr-generator"
                  data-testid="profile-share-qr"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl border border-[#00E5FF]/40 hover:border-[#00E5FF] hover:bg-[#00E5FF]/[0.08] font-mono-hy text-[10px] tracking-[0.22em] text-[#00E5FF] transition-all"
                >
                  <QrCode className="w-4 h-4" />
                  MAKE A QR
                </Link>
              </div>
              <p className="mt-4 font-body text-sm text-white/70 leading-relaxed">
                Every QR code you generate while signed in automatically embeds this referral link — so every scan is a chance to earn more credits.
              </p>
            </div>

            {/* ── Referral list ──────────────────────────────── */}
            <div className="rounded-3xl border border-white/[0.08] bg-white/[0.02] overflow-hidden" data-testid="profile-referral-list">
              <div className="px-5 sm:px-7 py-4 border-b border-white/[0.06] flex items-center justify-between">
                <div className="font-mono-hy text-[10px] text-white tracking-[0.24em]">
                  RECENT REFERRALS
                </div>
                <div className="font-mono-hy text-[9px] text-white/60 tracking-widest">
                  {list.length} TOTAL
                </div>
              </div>
              {list.length === 0 ? (
                <div className="px-5 sm:px-7 py-14 text-center" data-testid="profile-referral-empty">
                  <div className="mx-auto w-14 h-14 rounded-full border border-[#F2C94C]/30 bg-[#F2C94C]/[0.05] flex items-center justify-center mb-4">
                    <Users className="w-5 h-5 text-[#F2C94C]" />
                  </div>
                  <div className="font-display text-lg mb-1">No referrals yet.</div>
                  <p className="font-body text-sm text-white/70 max-w-md mx-auto">
                    Share your link or drop a QR code with a friend. You'll see them appear here the moment they sign up.
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-white/[0.05]">
                  {list.map((r, i) => (
                    <li
                      key={`${r.created_at}-${i}`}
                      data-testid="profile-referral-row"
                      className="px-5 sm:px-7 py-3.5 flex items-center gap-4"
                    >
                      <div className="w-8 h-8 rounded-full border border-[#27AE60]/30 bg-[#27AE60]/[0.08] flex items-center justify-center flex-shrink-0">
                        <Users className="w-3.5 h-3.5 text-[#27AE60]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-[13px] text-white truncate">
                          {r.referred_email_masked || "friend@***"}
                        </div>
                        <div className="font-mono-hy text-[9px] text-white/60 tracking-widest mt-0.5">
                          {formatDate(r.created_at)}
                        </div>
                      </div>
                      <div className="font-mono-hy text-[10px] text-[#27AE60] tracking-[0.18em] flex-shrink-0">
                        +{r.credits_earned ?? 10} CR
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* ── CTA ─────────────────────────────────────────── */}
            <div className="rounded-3xl border border-white/[0.08] bg-white/[0.02] p-5 sm:p-7 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="font-display text-xl sm:text-2xl mb-1">
                  Ready to level up?
                </div>
                <p className="font-body text-sm text-white/70">
                  Fold your referral link into every share — a QR code on a poster,
                  a link in your bio, a scannable sticker. Turn every scan into
                  fresh credits.
                </p>
              </div>
              <Link
                to="/qr-generator"
                data-testid="profile-cta-qr"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-[#F2C94C] hover:bg-[#F2D06B] text-[#0A0A0A] font-mono-hy text-[10px] tracking-[0.22em] font-bold hover:shadow-[0_0_28px_-4px_rgba(242,201,76,0.85)] transition-all"
              >
                MINT A QR CODE
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}

/* ── Helpers ─────────────────────────────────────────────────────── */
function StatCard({ icon, label, value, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-30 blur-2xl"
        style={{ background: accent }}
      />
      <div className="relative flex items-center gap-2 mb-3">
        <span style={{ color: accent }}>{icon}</span>
        <span className="font-mono-hy text-[9px] text-white/70 tracking-[0.24em]">
          {label}
        </span>
      </div>
      <div
        className="relative font-display text-4xl sm:text-5xl leading-none tracking-tight"
        style={{ color: accent }}
      >
        {value}
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "";
  }
}
